<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { NSpace, NSwitch, NRadioGroup, NRadioButton, NText } from 'naive-ui';
import { formatTime, formatDateTime } from '@awesomeclocks/utils';
import type { ClockWidgetConfig } from '@awesomeclocks/types';
import WidgetWrapper from './WidgetWrapper.vue';

interface Props {
  widget: ClockWidgetConfig;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  remove: [];
  updateName: [name: string];
  updateSettings: [settings: ClockWidgetConfig['settings']];
}>();

const currentTime = ref(new Date());
let intervalId: number | null = null;

const displayTime = computed(() => {
  if (props.widget.settings.displayType === 'digital') {
    return formatTime(
      currentTime.value,
      props.widget.settings.format,
      props.widget.settings.showSeconds
    );
  }
  return null;
});

const displayDate = computed(() => {
  if (props.widget.settings.showDate) {
    return formatDateTime(
      currentTime.value,
      'ddd, MMM DD, YYYY',
      props.widget.settings.format
    )
      .split(' ')
      .slice(0, 3)
      .join(' ');
  }
  return null;
});

const updateTime = () => {
  currentTime.value = new Date();
};

onMounted(() => {
  intervalId = window.setInterval(updateTime, 1000);
});

onUnmounted(() => {
  if (intervalId !== null) {
    clearInterval(intervalId);
  }
});

const updateFormat = (format: '12h' | '24h') => {
  emit('updateSettings', { ...props.widget.settings, format });
};

const updateShowSeconds = (showSeconds: boolean) => {
  emit('updateSettings', { ...props.widget.settings, showSeconds });
};

const updateShowDate = (showDate: boolean) => {
  emit('updateSettings', { ...props.widget.settings, showDate });
};

const updateDisplayType = (displayType: 'analog' | 'digital') => {
  emit('updateSettings', { ...props.widget.settings, displayType });
};
</script>

<template>
  <WidgetWrapper
    :widget="widget"
    @remove="emit('remove')"
    @update-name="emit('updateName', $event)"
  >
    <NSpace vertical align="center" :size="20">
      <!-- Clock Display -->
      <div v-if="widget.settings.displayType === 'digital'" class="digital-clock">
        <NText class="time-display" :depth="1">{{ displayTime }}</NText>
        <NText v-if="displayDate" class="date-display" :depth="3">{{
          displayDate
        }}</NText>
      </div>

      <div v-else class="analog-clock">
        <div class="clock-face">
          <div
            class="hand hour-hand"
            :style="{
              transform: `rotate(${(currentTime.getHours() % 12) * 30 + currentTime.getMinutes() * 0.5}deg)`,
            }"
          />
          <div
            class="hand minute-hand"
            :style="{
              transform: `rotate(${currentTime.getMinutes() * 6}deg)`,
            }"
          />
          <div
            v-if="widget.settings.showSeconds"
            class="hand second-hand"
            :style="{
              transform: `rotate(${currentTime.getSeconds() * 6}deg)`,
            }"
          />
          <div class="center-dot" />
        </div>
        <NText v-if="displayDate" class="date-display" :depth="3">{{
          displayDate
        }}</NText>
      </div>

      <!-- Settings -->
      <NSpace vertical :size="8" class="settings">
        <NSpace align="center" justify="space-between">
          <NText>Type:</NText>
          <NRadioGroup
            :value="widget.settings.displayType"
            size="small"
            @update:value="updateDisplayType"
          >
            <NRadioButton value="digital">Digital</NRadioButton>
            <NRadioButton value="analog">Analog</NRadioButton>
          </NRadioGroup>
        </NSpace>

        <NSpace align="center" justify="space-between">
          <NText>Format:</NText>
          <NRadioGroup
            :value="widget.settings.format"
            size="small"
            @update:value="updateFormat"
          >
            <NRadioButton value="12h">12h</NRadioButton>
            <NRadioButton value="24h">24h</NRadioButton>
          </NRadioGroup>
        </NSpace>

        <NSpace align="center" justify="space-between">
          <NText>Show Seconds:</NText>
          <NSwitch
            :value="widget.settings.showSeconds"
            @update:value="updateShowSeconds"
          />
        </NSpace>

        <NSpace align="center" justify="space-between">
          <NText>Show Date:</NText>
          <NSwitch :value="widget.settings.showDate" @update:value="updateShowDate" />
        </NSpace>
      </NSpace>
    </NSpace>
  </WidgetWrapper>
</template>

<style scoped>
.digital-clock {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.time-display {
  font-size: 3rem;
  font-weight: 300;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.05em;
}

.date-display {
  font-size: 1rem;
  margin-top: 4px;
}

.analog-clock {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.clock-face {
  position: relative;
  width: 200px;
  height: 200px;
  border: 3px solid currentColor;
  border-radius: 50%;
  opacity: 0.8;
}

.hand {
  position: absolute;
  bottom: 50%;
  left: 50%;
  transform-origin: bottom center;
  background: currentColor;
  border-radius: 4px;
}

.hour-hand {
  width: 6px;
  height: 50px;
  margin-left: -3px;
  opacity: 0.8;
}

.minute-hand {
  width: 4px;
  height: 70px;
  margin-left: -2px;
  opacity: 0.9;
}

.second-hand {
  width: 2px;
  height: 80px;
  margin-left: -1px;
  background: #f56c6c;
}

.center-dot {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 12px;
  height: 12px;
  background: currentColor;
  border-radius: 50%;
  transform: translate(-50%, -50%);
}

.settings {
  width: 100%;
  max-width: 300px;
  padding: 12px;
  margin-top: 12px;
}
</style>
